import { createContext, useContext, useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { cacheValue, readCachedValue } from '@/lib/offlineCache';

const STORAGE_KEY = 'selectedShowId';

/**
 * The sentinel for "All shows".
 *
 * Deliberately not an empty string: empty already means "nothing chosen yet"
 * in the stored value, and the two need to stay distinguishable.
 */
export const ALL_SHOWS = '__all__';

const SelectedShowContext = createContext(null);

/**
 * Holds the one shared answer to "which show is selected" for every consumer
 * at once. This used to be a plain hook that each caller invoked separately,
 * so Home and the ShowSelector inside it each held their own independent
 * useState: picking a new show updated the selector's own copy and
 * localStorage, but Home's copy never re-rendered, so the contact list kept
 * filtering by the old show until the page was reloaded. A Context makes
 * every consumer read the same state, so a change anywhere is visible
 * everywhere immediately.
 */
export function SelectedShowProvider({ children }) {
  const { data: allShows = [], isLoading } = useQuery({
    queryKey: ['shows'],
    queryFn: async () => {
      try {
        const list = await base44.entities.TradeShow.list('-start_date', 50);
        cacheValue('shows', list);
        return list;
      } catch (err) {
        // A cold start with no signal at all must still have somewhere for a
        // capture to be filed, so this is the one failure mode that falls
        // back to the last-known list rather than surfacing the error.
        if (err.status === 0) return readCachedValue('shows', []);
        throw err;
      }
    },
  });

  // This query answers "every show that exists" because Shows.jsx, Templates.jsx,
  // DeletedContacts.jsx and EditContact.jsx all share it and all need
  // archived shows too. A new capture must never default into one, and an
  // archived show sorts ahead of an active one whenever it started more
  // recently, so it is filtered out here rather than in the shared query.
  const shows = allShows.filter((s) => s.is_active !== false);

  const [selectedShowId, setSelectedShowIdState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || '',
  );

  // Keep the selection valid: fall back to the most recent show. "All shows"
  // is a legitimate value, so it must not be treated as stale and reset.
  useEffect(() => {
    if (shows.length === 0) return;
    if (selectedShowId === ALL_SHOWS) return;
    const exists = shows.find((s) => s.id === selectedShowId);
    if (!exists) {
      const fallback = shows[0].id;
      localStorage.setItem(STORAGE_KEY, fallback);
      setSelectedShowIdState(fallback);
    }
  }, [shows, selectedShowId]);

  const setSelectedShowId = (id) => {
    localStorage.setItem(STORAGE_KEY, id);
    setSelectedShowIdState(id);
  };

  const isAllShows = selectedShowId === ALL_SHOWS;

  // Always a real show, never null while any show exists, so a capture always
  // has somewhere to go. With "All shows" showing, that is the most recent
  // one, and the capture screen shows which it is so the rep can change it.
  const selectedShow = isAllShows
    ? (shows[0] ?? null)
    : (shows.find((s) => s.id === selectedShowId) || null);

  const value = { shows, selectedShow, selectedShowId, setSelectedShowId, isAllShows, isLoading };

  return (
    <SelectedShowContext.Provider value={value}>
      {children}
    </SelectedShowContext.Provider>
  );
}

/**
 * Which show the app is looking at.
 *
 * Two different questions come out of this hook and they are not the same:
 *
 *   selectedShowId / isAllShows  what to FILTER the contact list by, which
 *                                can be every show at once
 *   selectedShow                 which show a NEW CAPTURE is filed into, which
 *                                can never be "all", because a contact belongs
 *                                to exactly one show
 *
 * Keeping them apart is what stops a lead captured while browsing "All shows"
 * from being filed nowhere. See NewMeeting.jsx, which reads selectedShow.
 *
 * Must be used inside a SelectedShowProvider, mounted once around the
 * authenticated part of the app, so every caller shares the same state.
 */
export function useSelectedShow() {
  const ctx = useContext(SelectedShowContext);
  if (!ctx) {
    throw new Error('useSelectedShow must be used within a SelectedShowProvider');
  }
  return ctx;
}
